import { Box } from '@televet/kibble-ui';
import { Button } from '@televet/kibble-ui';
import { Divider } from '@televet/kibble-ui';
import { Heading } from '@televet/kibble-ui';
import { Modal } from '@televet/kibble-ui';
import { ModalBody } from '@televet/kibble-ui';
import { ModalCloseButton } from '@televet/kibble-ui';
import { ModalFooter } from '@televet/kibble-ui';
import { ModalHeader } from '@televet/kibble-ui';
import { Spinner } from '@televet/kibble-ui';
import { Text } from '@televet/kibble-ui';
import React, { useEffect } from 'react';
import { useTracking } from 'shared/providers/TrackingProvider';
import { AiMemoEvents } from 'shared/providers/TrackingProvider/EventNames';
import { useFindFirstCallRecordingQuery } from 'shared/types/graphql';

type CallRecordingTranscriptSummaryProps = {
  isTranscriptionSummaryModalOpen: boolean;
  onTranscriptionSummaryModalClose: () => void;
  callHistoryId: string;
};

const CallRecordingTranscriptSummary = ({
  isTranscriptionSummaryModalOpen,
  onTranscriptionSummaryModalClose,
  callHistoryId,
}: CallRecordingTranscriptSummaryProps): JSX.Element => {
  const { trackEvent } = useTracking();
  const { data: callRecordingData, loading: isCallRecordingDataLoading } = useFindFirstCallRecordingQuery({
    variables: { where: { callHistoryId: { equals: callHistoryId } } },
    skip: !isTranscriptionSummaryModalOpen,
  });

  useEffect(() => {
    if (isTranscriptionSummaryModalOpen) {
      trackEvent(AiMemoEvents.SummaryOpen, { callHistoryId });
    }
  }, [callHistoryId, isTranscriptionSummaryModalOpen, trackEvent]);

  const date = new Date(callRecordingData?.findFirstCallRecording?.callHistory.createdAt);
  const dateString = date.toLocaleDateString();
  const timeString = date.toLocaleTimeString();
  const from = callRecordingData?.findFirstCallRecording?.callHistory.from;
  const to = callRecordingData?.findFirstCallRecording?.callHistory.to;
  const filename = `${from}_${to}_${dateString}_${timeString}.txt`;

  const summary = callRecordingData?.findFirstCallRecording?.summary || 'No summary available';
  const transcription = callRecordingData?.findFirstCallRecording?.transcription || 'No transcription available';

  const handleDownload = (): void => {
    trackEvent(AiMemoEvents.Download, { callHistoryId });
    const downloadLink = document.createElement('a');
    const blob = new Blob(
      [
        `Call Summary and Transcription\n\nDate: ${dateString}\nTime:${timeString}\n\nSummary\n\n${summary}\n\nTranscription\n\n${transcription}`,
      ],
      { type: 'text/plain' },
    );

    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.download = filename;

    document.body.appendChild(downloadLink); // Append link to the body
    downloadLink.click(); // Programmatically click the link to trigger the download
    document.body.removeChild(downloadLink);
  };

  return (
    <Modal size="4xl" isOpen={isTranscriptionSummaryModalOpen} onClose={onTranscriptionSummaryModalClose}>
      <ModalCloseButton />
      <ModalHeader>Call Summary and Transcription</ModalHeader>
      <ModalBody maxH="80vh" minH="80vh" overflow="auto">
        {isCallRecordingDataLoading ? (
          <Spinner />
        ) : (
          <Box>
            <Text as="p">
              <Text fontWeight="bold">Call Date:</Text> {dateString}
            </Text>
            <Text as="p">
              <Text fontWeight="bold">Call Time:</Text> {timeString}
            </Text>
            <Heading size="md" mt="4">
              Summary
            </Heading>
            <Text as="p" mt="4" whiteSpace="pre-wrap">
              {summary}
            </Text>
            <Divider my="6" />
            <Heading size="md">Transcription</Heading>
            <Text whiteSpace="pre-wrap">{transcription}</Text>
          </Box>
        )}
      </ModalBody>
      <ModalFooter>
        <Button isFullWidth iconName="download" onClick={handleDownload} variant="tertiary">
          Download as .txt
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CallRecordingTranscriptSummary;
